import type { Post } from "./types";
import heroKronofogden from "@/assets/news/sms-lan-trots-kronofogden.jpg";
import bildRadgivning from "@/assets/news/privatlan-lagst-ranta-2026.jpg";

export const POSTS_11: Record<string, Post> = {
  "sms-lan-trots-kronofogden": {
    title: "SMS lån trots Kronofogden – vad som faktiskt gäller 2026",
    intro:
      "Söker du sms lån trots kronofogden är du sannolikt i en pressad situation, och vi tänker inte moralisera över hur du hamnat där. Vi förstår att en skuld hos Kronofogden känns som att alla dörrar stängs samtidigt. Här får du en ärlig genomgång: vilka långivare som faktiskt kan säga ja, varför de allra flesta säger nej, vad ett sms lån kostar 2026 – och vilka alternativ som nästan alltid är billigare och tryggare än ett nytt lån ovanpå en gammal skuld.",
    author: "Erik Lindqvist",
    authorTitle: "finansiell skribent, 9 år inom konsumentkrediter",
    published: "24 augusti 2026",
    updated: "24 augusti 2026",
    heroSrc: heroKronofogden,
    heroAlt: "Person går igenom räkningar vid köksbordet och söker sms lån trots kronofogden",
    topLenderId: "northmill",
    bottomLenderId: "credifi",
    topLenderTitle: "Populärast bland dig med tidigare avslag",
    bottomLenderTitle: "Vår rekommendation vid akut behov",
    cta: { to: "/lan-utan-uc", label: "Jämför långivare utan UC" },
    calculator: {
      title: "Räkna på sms lån trots Kronofogden – lånekalkylator",
      intro:
        "Innan du ansöker: se vad lånet faktiskt kostar. Dra i reglagen för att se månadskostnad, räntekostnad, total lånekostnad och effektiv ränta.",
      note:
        "Månadskostnaden avgörs av lånebelopp, löptid och nominell ränta, men det är den effektiva räntan du ska jämföra – den räknar även in uppläggningsavgift och aviavgift. Har du en skuld hos Kronofogden är regeln enkel: låna det minsta belopp som löser det akuta problemet och välj den kortaste löptid din budget klarar, så blir den totala lånekostnaden lägst.",
      amount: 6000,
      minAmount: 1000,
      maxAmount: 50000,
      months: 12,
      maxMonths: 60,
      rate: 24,
      minRate: 6,
      maxRate: 39,
      setupFee: 350,
      invoiceFee: 29,
    },
    faq: [
      {
        q: "Kan man få sms lån trots Kronofogden?",
        a: "Med ett pågående mål hos Kronofogden säger nästan alla seriösa långivare nej, eftersom kreditprövningen visar att återbetalningsförmågan redan är ansträngd. Är skulden slutbetald och ärendet avslutat ökar chanserna successivt, särskilt hos långivare som använder Creditsafe i stället för UC.",
      },
      {
        q: "Hur länge ligger en skuld hos Kronofogden kvar?",
        a: "Uppgiften om ett avslutat ärende ligger normalt kvar i kreditupplysningen i tre år för privatpersoner, räknat från det år ärendet avslutades. Betalningsanmärkningen försvinner alltså inte i samma stund som du betalar, men den väger allt lättare med tiden.",
      },
      {
        q: "Får långivaren se att jag finns hos Kronofogden?",
        a: "Ja. Uppgifter om betalningsförelägganden och utmätning finns i kreditupplysningen hos både UC, Creditsafe och Dun & Bradstreet. Att välja en långivare utan UC döljer inte skulden – det är en annan riskmodell, inte ett annat register över skulder.",
      },
      {
        q: "Kan jag låna pengar med skuld hos Kronofogden om jag har fast inkomst?",
        a: "Fast inkomst hjälper, men om du har en pågående utmätning är utrymmet i kalkylen redan intecknat och de flesta långivare avslår ändå. Vissa mindre kreditgivare gör undantag vid små belopp och stabil inkomst, men räntan blir då hög.",
      },
      {
        q: "Vad händer om jag tar ett lån och inte kan betala?",
        a: "Ärendet går till inkasso, sedan till Kronofogden, och du får ytterligare en betalningsanmärkning plus inkasso- och förseningsavgifter. Skulden växer och en ny anmärkning startar om treårsklockan. Kontakta alltid långivaren innan förfallodagen och be om anstånd.",
      },
      {
        q: "Finns det gratis hjälp att få?",
        a: "Ja. Budget- och skuldrådgivningen i din kommun är kostnadsfri och har tystnadsplikt. De hjälper dig med budget, kontakt med fordringsägare och ansökan om skuldsanering hos Kronofogden. Många upplever att ett samtal där ger mer än ett nytt lån.",
      },
      {
        q: "Är skuldsanering ett bättre alternativ?",
        a: "Om skulderna är så stora att du inte kan betala dem inom överskådlig tid är skuldsanering ofta den enda hållbara vägen. Du lever på existensminimum i normalt fem år, men blir sedan skuldfri. Under pågående skuldsanering får du inte ta nya krediter.",
      },
    ],
    blocks: [
      { t: "p", text: "Vi börjar med det du behöver veta direkt: att söka sms lån trots kronofogden är svårt, men inte alltid omöjligt. Det avgörande är om ditt ärende är pågående eller avslutat. Med en pågående utmätning säger i princip alla långivare med tillstånd nej. Är skulden betald och ärendet avslutat finns det långivare som ser till din inkomst i dag i stället för till din historik – och de använder oftast Creditsafe i stället för UC." },
      { t: "p", text: "Vi vet att den informationen kan kännas som ännu ett nej. Därför ägnar vi större delen av den här guiden åt vad du faktiskt kan göra, i vilken ordning, och vad varje väg kostar i kronor." },

      { t: "h2", text: "Sms lån trots kronofogden – kort svar först" },
      { t: "p", text: "Ett smslån är ett litet lån utan säkerhet, normalt 1 000–50 000 kr, med digital ansökan och snabb utbetalning. Att ha en skuld hos Kronofogden är inte ett formellt förbud mot att låna, men det är en tung minuspost i kreditprövningen som långivaren enligt konsumentkreditlagen är skyldig att göra." },
      {
        t: "stats",
        items: [
          { label: "Pågående mål", value: "Nästan alltid avslag" },
          { label: "Avslutat ärende", value: "Chans efter 6–36 mån" },
          { label: "Räntetak 2026", value: "Referensränta + 20 p.e." },
        ],
      },
      { t: "h3", text: "Skillnaden mellan pågående och avslutat ärende" },
      { t: "p", text: "Ett pågående mål betyder att Kronofogden aktivt driver in en skuld, ofta genom löneutmätning. Då är ditt ekonomiska utrymme redan uträknat av myndigheten och det finns per definition inget kvar att betala ett nytt lån med. Ett avslutat ärende betyder att skulden är reglerad – uppgiften ligger kvar i kreditupplysningen, men din faktiska betalningsförmåga är återställd." },
      { t: "h3", text: "Därför säger långivaren nej just nu" },
      { t: "p", text: "Kreditgivaren gör en så kallad kvar att leva på-kalkyl. Har du löneutmätning är det Kronofogden som redan har tagit hela överskottet. Långivaren ser då att en ny månadskostnad inte ryms, och ett ja hade varit ett lagbrott snarare än en tjänst." },

      {
        t: "box",
        tone: "warning",
        title: "Varningsflaggor – aktörer att undvika helt",
        items: [
          "Löften om \"garanterat lån oavsett kreditvärdighet\"",
          "Krav på avgift i förskott innan lånet betalas ut",
          "Långivare utan tillstånd från Finansinspektionen",
          "Ingen effektiv ränta angiven i erbjudandet",
          "Endast utländskt telefonnummer eller chatt som kontaktväg",
          "Erbjudanden i sms eller sociala medier du inte själv sökt upp",
        ],
      },

      { t: "h2", text: "Så fungerar Kronofogden och betalningsanmärkningen" },
      { t: "p", text: "Många blandar ihop begreppen, och det är begripligt – de dyker upp samtidigt när det redan är stressigt. Här är kedjan i klartext." },
      {
        t: "steps",
        title: "Från obetald räkning till anmärkning",
        items: [
          "Räkningen förfaller och en påminnelseavgift läggs på.",
          "Ärendet går till inkasso, som lägger på lagstadgad inkassoavgift.",
          "Fordringsägaren ansöker om betalningsföreläggande hos Kronofogden.",
          "Du får ett brev och kan bestrida inom angiven tid.",
          "Bestrider du inte meddelas utslag – och en betalningsanmärkning registreras.",
          "Kan du inte betala inleds utmätning av lön eller tillgångar.",
        ],
      },
      { t: "p", text: "Poängen med kedjan är att den går att bryta i varje steg. Ett samtal till fordringsägaren i steg ett eller två är den absolut billigaste åtgärd som finns – och den kräver inget lån. Läs mer om hur processen fungerar hos [Kronofogden](https://www.kronofogden.se)." },
      { t: "h3", text: "Hur länge syns uppgiften?" },
      {
        t: "table",
        caption: "Så länge ligger uppgifterna kvar i kreditupplysningen",
        head: ["Uppgift", "Ligger kvar", "Påverkan på lån"],
        rows: [
          ["Inkassokrav (ej hos KFM)", "Syns inte i upplysningen", "Ingen direkt"],
          ["Betalningsanmärkning, privatperson", "3 år", "Hög"],
          ["Pågående löneutmätning", "Under hela perioden", "Mycket hög"],
          ["Skuldsaneringsbeslut", "5 år", "Total spärr"],
          ["Avslutat ärende, skulden betald", "3 år från avslut", "Avtagande"],
        ],
      },
      { t: "h3", text: "Kan man låna med skuld hos kronofogden – ärligt svar" },
      { t: "p", text: "Ja, i teorin. Nej, i de flesta praktiska fall. Och även när svaret är ja bör frågan vara en annan: gör det här min situation bättre om tolv månader? Ett smslån med hög effektiv ränta ovanpå en befintlig skuld är i nio fall av tio en fördröjning, inte en lösning." },

      { t: "img", src: bildRadgivning, alt: "Rådgivare går igenom budget med person som vill låna pengar med skuld hos kronofogden" },

      { t: "h2", text: "Låna pengar med skuld hos kronofogden – vilka vägar finns?" },
      { t: "p", text: "Vi har rangordnat alternativen efter hur ofta de faktiskt hjälper, inte efter hur snabbt de ger pengar. De två första kostar ingenting." },
      {
        t: "steps",
        title: "Gör i den här ordningen",
        items: [
          "Ring fordringsägaren eller inkassobolaget och begär avbetalningsplan eller anstånd.",
          "Boka tid hos budget- och skuldrådgivningen i din kommun – kostnadsfritt och utan krav.",
          "Se över om du har rätt till bostadsbidrag, försörjningsstöd eller andra ersättningar.",
          "Undersök om en anhörig kan gå in som medsökande på ett vanligt privatlån.",
          "Först därefter: jämför långivare som prövar utan UC, och bara för minsta möjliga belopp.",
        ],
      },
      { t: "h3", text: "Långivare utan UC prövar dig på andra grunder" },
      { t: "p", text: "Långivare som använder Creditsafe eller Dun & Bradstreet har egna scoringmodeller. Det innebär inte att de blundar för en skuld hos Kronofogden, men de väger antalet tidigare kreditförfrågningar lättare. Har du fått nej på grund av många förfrågningar snarare än på grund av anmärkningen kan det göra skillnad. Se hela vår genomgång av [lån utan UC](/lan-utan-uc) och de aktuella villkoren." },
      { t: "h3", text: "Lån trots betalningsanmärkning utan pågående mål" },
      { t: "p", text: "Har du en eller två äldre anmärkningar men inget pågående ärende är läget helt annat. Flera långivare beviljar då lån, förutsatt att inkomsten är regelbunden. Vår guide om [betalningsanmärkning och lån](/nyhet/betalningsanmarkning-lan) går igenom exakt vilka krav som brukar gälla." },
      { t: "h3", text: "Medsökande höjer chansen mest" },
      { t: "p", text: "Två inkomster i samma ansökan förbättrar kalkylen mer än något annat du kan göra på kort sikt, och sänker dessutom räntan. Var samtidigt ärlig med vad det innebär: medsökanden blir solidariskt betalningsansvarig för hela skulden." },

      {
        t: "table",
        caption: "Ungefärlig chans att bli beviljad 2026",
        head: ["Din situation", "Vanlig bank", "Långivare utan UC"],
        rows: [
          ["Pågående löneutmätning", "Ingen", "Mycket låg"],
          ["Pågående skuldsanering", "Ingen", "Ingen"],
          ["Avslutat ärende, 0–12 mån sedan", "Mycket låg", "Låg"],
          ["Avslutat ärende, 1–3 år sedan", "Låg", "Måttlig"],
          ["Enbart äldre anmärkning, fast inkomst", "Mycket låg", "God"],
          ["Många UC-förfrågningar, inga anmärkningar", "Låg", "God"],
        ],
      },

      { t: "h2", text: "Vad ett smslån kostar 2026" },
      { t: "p", text: "Två regler i konsumentkreditlagen sätter ramen för alla svenska konsumentkrediter, och de gäller oavsett vilket kreditupplysningsbolag långivaren använder." },
      { t: "h3", text: "Räntetaket" },
      { t: "p", text: "Den nominella räntan får aldrig överstiga Riksbankens referensränta plus 20 procentenheter. Det innebär att de extrema räntor som fanns på marknaden före 2018 inte längre är lagliga. Referensräntan fastställs halvårsvis av [Riksbanken](https://www.riksbank.se)." },
      { t: "h3", text: "Kostnadstaket" },
      { t: "p", text: "Ränta, avgifter och dröjsmålsränta får tillsammans aldrig överstiga det belopp du lånat. Lånar du 6 000 kr kan de samlade kostnaderna alltså aldrig bli mer än 6 000 kr, hur länge skulden än ligger kvar. Reglerna övervakas av [Finansinspektionen](https://www.fi.se) och [Konsumentverket](https://www.konsumentverket.se)." },
      {
        t: "table",
        caption: "Räkneexempel: 6 000 kr med 24 % nominell ränta, 350 kr uppläggning, 29 kr avi",
        head: ["Löptid", "Månadskostnad", "Total lånekostnad", "Effektiv ränta"],
        rows: [
          ["6 mån", "1 100 kr", "600 kr", "40,4 %"],
          ["12 mån", "600 kr", "1 190 kr", "36,2 %"],
          ["24 mån", "348 kr", "2 350 kr", "33,1 %"],
          ["36 mån", "265 kr", "3 550 kr", "31,8 %"],
        ],
      },
      { t: "p", text: "Mönstret är viktigare än de exakta siffrorna: dubbla löptiden och totalkostnaden ungefär dubbleras. Månadskostnaden ser bättre ut, men du betalar väsentligt mer. Räkna själv i vår [lånekalkyl](/guider/lanekalkyl) innan du bestämmer dig." },
      { t: "h3", text: "Effektiv ränta smslån – det enda jämförelsemåttet" },
      { t: "p", text: "Effektiv ränta innehåller nominell ränta, uppläggningsavgift och aviavgift omräknat till årsbasis. Två lån med samma nominella ränta kan skilja tio procentenheter i effektiv ränta enbart på grund av avgifterna. Jämför aldrig något annat." },

      {
        t: "box",
        tone: "info",
        title: "Fem frågor att ställa innan du signerar",
        items: [
          "Vad är den effektiva räntan för just mitt belopp och min löptid?",
          "Vad kostar lånet totalt i kronor?",
          "Vilka avgifter tillkommer vid försenad betalning?",
          "Får jag lösa lånet i förtid utan extra kostnad?",
          "Står långivaren under Finansinspektionens tillsyn?",
        ],
      },

      { t: "h2", text: "Lån vid skuldsanering – vad lagen säger" },
      { t: "p", text: "Under pågående skuldsanering får du inte ta nya krediter. Det är ett villkor i beslutet, och att bryta mot det kan leda till att skuldsaneringen upphävs. Vi vet att fem år på existensminimum låter avskräckande, men för den som har skulder som aldrig kan betalas är det den enda vägen till att faktiskt bli skuldfri." },
      { t: "h3", text: "Efter avslutad skuldsanering" },
      { t: "p", text: "När perioden är över försvinner uppgifterna successivt och du kan börja bygga upp kreditvärdigheten igen. Ett litet, skött abonnemang eller en liten kredit som betalas i tid varje månad återuppbygger historiken snabbare än de flesta tror." },

      { t: "h2", text: "Så bygger du tillbaka kreditvärdigheten" },
      {
        t: "ul",
        items: [
          "Betala allt i tid – betalningshistoriken är den tyngsta faktorn.",
          "Avsluta krediter du inte använder; outnyttjat kreditutrymme räknas mot dig.",
          "Sprid inte ut ansökningar hos många långivare på kort tid.",
          "Folkbokför dig rätt och håll adressuppgifterna aktuella.",
          "Begär ut din egen kreditupplysning kostnadsfritt och kontrollera att den stämmer.",
          "Öka den taxerade inkomsten om möjligt – även deltid räknas.",
        ],
      },
      { t: "h3", text: "Rätta felaktiga uppgifter" },
      { t: "p", text: "Det förekommer att anmärkningar registrerats felaktigt eller ligger kvar för länge. Du har rätt att begära rättelse hos kreditupplysningsbolaget. Det kostar ingenting och kan göra stor skillnad." },

      {
        t: "proscons",
        title: "Smslån när du har haft skuld hos Kronofogden",
        pros: [
          "Snabb utbetalning, ofta inom minuter",
          "Ansökan dygnet runt med BankID",
          "Vissa långivare prövar utan UC",
          "Kostnadstak och räntetak skyddar dig i lag",
          "Ångerrätt i 14 dagar",
        ],
        cons: [
          "Betydligt högre ränta än vanliga privatlån",
          "Risk att en gammal skuld byts mot en ny och dyrare",
          "Låga maxbelopp vid svag kredithistorik",
          "Ny anmärkning startar om treårsperioden",
          "Frestande att lösa löpande utgifter med lån",
        ],
      },

      { t: "h2", text: "Alternativ som nästan alltid är billigare" },
      { t: "p", text: "Innan du tar ett dyrt lån – gå igenom den här listan. Flera av punkterna kostar noll kronor." },
      {
        t: "ul",
        items: [
          "Avbetalningsplan direkt med fordringsägaren eller inkassobolaget.",
          "Kostnadsfri budget- och skuldrådgivning i din kommun.",
          "Anstånd med hyra, el eller försäkring – de flesta beviljar en månad.",
          "Samla dyra krediter i ett billigare [samlingslån](/samla-lan) om du inte har aktiva anmärkningar.",
          "Sälj något du inte behöver – ofta snabbare än en låneansökan.",
          "Fråga en anhörig om ett räntefritt lån med skriftligt avtal.",
        ],
      },
      { t: "h3", text: "Budget- och skuldrådgivning är underanvänd" },
      { t: "p", text: "Rådgivningen är gratis, kommunal och har tystnadsplikt. De kan förhandla med fordringsägare, hjälpa dig ansöka om skuldsanering och göra en realistisk budget. Vi rekommenderar det som första steg i praktiskt taget alla lägen där Kronofogden är inblandad." },

      { t: "h2", text: "Vanliga misstag vi ser" },
      {
        t: "ul",
        items: [
          "Att söka hos tio långivare samma kväll – varje avslag försämrar nästa ansökan.",
          "Att låna till löpande utgifter i stället för att göra om budgeten.",
          "Att välja längsta möjliga löptid för att månadskostnaden ska se snäll ut.",
          "Att jämföra nominell ränta i stället för effektiv ränta.",
          "Att ignorera brev från Kronofogden – bestridandetiden är kort.",
          "Att tro att ett lån utan UC gömmer skulden. Det gör det inte.",
        ],
      },

      { t: "h2", text: "Så jämför du långivare rättvist" },
      { t: "p", text: "Använd samma belopp och samma löptid hos alla, och notera effektiv ränta, uppläggningsavgift, aviavgift och utbetalningstid. Vår sida om [snabblån](/snabblan) och guiden om [privatlån](/privatlan) visar aktuella villkor sida vid sida." },
      { t: "h3", text: "Tre saker att kontrollera på varje långivare" },
      {
        t: "ul",
        items: [
          "Att bolaget finns i Finansinspektionens företagsregister.",
          "Att effektiv ränta anges tydligt i erbjudandet, inte bara i det finstilta.",
          "Att det finns svensk kundtjänst med telefonnummer.",
        ],
      },

      { t: "h2", text: "Vår slutsats" },
      { t: "p", text: "Har du ett pågående mål hos Kronofogden är ett smslån sällan vägen framåt – och de flesta långivare kommer ändå att säga nej. Är ärendet avslutat och du har regelbunden inkomst finns det långivare som prövar dig på andra grunder än storbankerna, och då kan ett mindre lån vara rimligt om du räknat på totalkostnaden först." },
      { t: "p", text: "Vad du än väljer: låna minsta möjliga belopp, välj kortast möjliga löptid du klarar, och ta det kostnadsfria samtalet med kommunens budgetrådgivare först. Vi hellre ser att du slipper låna än att du lånar hos oss." },

      { t: "h2", text: "Källor och tillsyn" },
      {
        t: "ul",
        items: [
          "[Finansinspektionen](https://www.fi.se) – tillstånd och tillsyn över kreditgivare.",
          "[Konsumentverket](https://www.konsumentverket.se) – räntetak, kostnadstak och marknadsföring.",
          "[Kronofogden](https://www.kronofogden.se) – betalningsförelägganden, utmätning och skuldsanering.",
          "[Riksbanken](https://www.riksbank.se) – referensräntan som styr räntetaket.",
          "Läs mer om vår metod på sidan [Hur vi granskar](/om-oss/hur-vi-granskar) och om oss i [redaktionen](/om-oss/redaktion).",
        ],
      },
    ],
  },
};
